import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface UserClinic {
  clinic_id: string;
  role: string;
  standard_role?: string | null;
  access_mode?: string | null;
  clinic: {
    id: string;
    name: string;
    code: string;
  };
}

export const useUserClinics = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-clinics", user?.id],
    queryFn: async (): Promise<UserClinic[]> => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("user_clinic_roles")
        .select(`
          clinic_id,
          role,
          standard_role,
          access_mode,
          clinic:clinics(id, name, code)
        `)
        .eq("user_id", user.id);

      if (error) throw error;
      
      // Filter out any entries with missing or invalid clinic data
      const validData = (data || []).filter(d => 
        d.clinic !== null && 
        typeof d.clinic === 'object' && 
        'name' in d.clinic && 
        typeof d.clinic.name === 'string'
      ) as UserClinic[];
      
      return validData;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Helper to get just the clinic IDs.
// Memoized so the returned arrays are reference-stable across renders — this
// matters for any consumer that puts `clinicIds` in a useEffect dep array,
// notably realtime channel setup (otherwise effects re-run on every render
// and racing channel teardown/setup throws "cannot add postgres_changes
// callbacks ... after subscribe()").
export const useUserClinicIds = () => {
  const { data: clinics, isLoading, error } = useUserClinics();

  const clinicsList = useMemo(() => clinics || [], [clinics]);

  // Deduplicate by clinic_id — users may have multiple roles per clinic
  const clinicIds = useMemo(
    () => [...new Set(clinicsList.filter(c => c.clinic_id).map(c => c.clinic_id))],
    [clinicsList]
  );

  // Deduplicate clinics list by clinic_id (keep first occurrence)
  const uniqueClinics = useMemo(
    () => clinicsList.filter((c, i, arr) => arr.findIndex(x => x.clinic_id === c.clinic_id) === i),
    [clinicsList]
  );

  return {
    clinicIds,
    clinics: uniqueClinics,
    hasData: clinics !== undefined,
    isLoading,
    error,
  };
};
